import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Newspaper, Search, ExternalLink, Loader2, ChevronDown, ChevronUp, BarChart2, TrendingUp, CheckCircle, XCircle, AlertCircle, Archive, RefreshCw, Download, Trash2, Clock, ShieldAlert, Activity } from 'lucide-react';
import { triggerFullCollect, fetchFullCollections, fetchFullCollection, matchPressToArchive, fetchKnfcPress, fetchKnfcPressSummary, scrapeKnfcPress, matchKnfcPressItem, removeKnfcMatchArticle, matchAllKnfcPress, downloadPressMatches, extractAuthors, fetchKnfcScraperStatus, fetchKnfcMatchJobs, fetchPressCoverageStats } from '../api';
import { useMutation } from '@tanstack/react-query';

const TYPE_LABEL = { domestic: '국내', overseas: '해외(국문)', column: '칼럼', foreign: '영문' };

function decodeHtml(str) {
  return (str || '')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]+>/g, '');
}

function deduplicateByTitle(articles) {
  const seen = new Set();
  return articles.filter((a) => {
    const key = decodeHtml(a.title).replace(/\s+/g, '').slice(0, 30);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function SourceBar({ articles }) {
  const counts = {};
  for (const a of articles) counts[a.source] = (counts[a.source] || 0) + 1;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const max = sorted[0]?.[1] || 1;
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <BarChart2 size={13} /> 매체별 수집 건수
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        {sorted.map(([src, cnt]) => (
          <div key={src} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '120px', fontSize: '0.75rem', color: '#94a3b8', textAlign: 'right', flexShrink: 0 }}>{src}</div>
            <div style={{ flex: 1, height: '14px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${(cnt / max) * 100}%`, height: '100%', background: '#6366f1', borderRadius: '3px', transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: '0.75rem', color: '#e2e8f0', width: '28px', flexShrink: 0 }}>{cnt}건</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CollectionDetail({ id }) {
  const { data, isLoading } = useQuery({
    queryKey: ['full-collection', id],
    queryFn: () => fetchFullCollection(id),
    enabled: !!id,
  });
  const [expanded, setExpanded] = useState(true);

  if (isLoading) return <div style={{ padding: '1rem', color: '#64748b' }}><div className="spinner" style={{ display: 'inline-block' }} /> 불러오는 중...</div>;
  if (!data) return null;

  const rawArticles = data.articles || [];
  const articles = deduplicateByTitle(rawArticles);
  const dupCount = rawArticles.length - articles.length;

  return (
    <div style={{ marginTop: '1rem' }}>
      <SourceBar articles={articles} />
      <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem', fontWeight: 600 }}>
        전체 {articles.length}건 {dupCount > 0 && <span style={{ color: '#475569', fontWeight: 400 }}>(중복 {dupCount}건 제거됨)</span>}
        <button onClick={() => setExpanded(v => !v)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', marginLeft: '0.5rem' }}>
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>
      {expanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {articles.map((a) => (
            <div key={a.id} style={{ padding: '0.6rem 0', borderBottom: '1px solid #1e293b', display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
                  <span style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: '3px', background: '#1e293b', color: '#64748b' }}>
                    {TYPE_LABEL[a.article_type] || a.article_type}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>{a.source}</span>
                  <span style={{ fontSize: '0.65rem', color: '#334155' }}>{a.published_at?.slice(0, 16)}</span>
                  <span style={{ fontSize: '0.65rem', color: '#94a3b8', marginLeft: 'auto' }}>★{a.relevance_score?.toFixed(1)}</span>
                </div>
                <a href={a.link} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: '0.875rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: '0.3rem', lineHeight: 1.45 }}>
                  {decodeHtml(a.title)}
                  <ExternalLink size={11} opacity={0.4} style={{ flexShrink: 0 }} />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KnfcArchivePage() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [openId, setOpenId] = useState(null);
  const [matchData, setMatchData] = useState({});
  const [matchLoading, setMatchLoading] = useState({});

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['knfc-press', page, q],
    queryFn: () => fetchKnfcPress({ page, limit: 20, q }),
  });

  const { data: summary, refetch: refetchSummary } = useQuery({
    queryKey: ['knfc-press-summary'],
    queryFn: fetchKnfcPressSummary,
  });

  const scrapeMut = useMutation({
    mutationFn: () => scrapeKnfcPress({ max_pages: 10 }),
    onSuccess: () => { setTimeout(() => { refetch(); refetchSummary(); }, 3000); },
  });

  const extractAuthorsMut = useMutation({
    mutationFn: extractAuthors,
    onSuccess: (data) => { alert(`기자명 추출 완료: 총 ${data.total}건 중 ${data.extracted}건 추출`); refetch(); },
  });

  async function handleMatchItem(item) {
    if (matchData[item.id]) { setOpenId(openId === item.id ? null : item.id); return; }
    setOpenId(item.id);
    setMatchLoading(p => ({ ...p, [item.id]: true }));
    try {
      const res = await matchPressToArchive({
        press_releases: [{ id: item.id, title: item.title, date: item.published_at }],
        date_range_days: 14,
      });
      setMatchData(p => ({ ...p, [item.id]: res.results?.[0] }));
    } catch {}
    setMatchLoading(p => ({ ...p, [item.id]: false }));
  }

  return (
    <div>
      {/* 상단 현황 + 스크래핑 버튼 */}
      <div className="card" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '1rem', flex: 1 }}>
          {[
            { label: '수집된 보도자료', value: summary?.total?.toLocaleString() ?? '-', color: '#e9d5ff' },
            { label: '최신 배포일', value: summary?.latest ?? '-', color: '#94a3b8' },
            { label: '최초 배포일', value: summary?.oldest ?? '-', color: '#475569' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.7rem', color: '#475569' }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => scrapeMut.mutate()} disabled={scrapeMut.isPending}
            style={{ background: '#7c3aed', border: 'none' }}>
            {scrapeMut.isPending ? <><Loader2 size={13} className="spin" /> 수집 중...</> : <><RefreshCw size={13} /> 최신 보도자료 수집</>}
          </button>
          <button className="btn btn-sm" onClick={() => extractAuthorsMut.mutate()} disabled={extractAuthorsMut.isPending}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8' }}>
            {extractAuthorsMut.isPending ? <><Loader2 size={13} className="spin" /> 추출 중...</> : <><Download size={13} /> 2021년 이후 기자명 추출</>}
          </button>
        </div>
        {scrapeMut.isSuccess && <span style={{ fontSize: '0.78rem', color: '#4ade80' }}>✅ 수집 완료 (결과 반영 중...)</span>}
      </div>

      {/* 검색 */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input className="input" placeholder="제목 검색..." value={qInput} onChange={e => setQInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setQ(qInput); setPage(1); } }}
          style={{ flex: 1 }} />
        <button className="btn btn-sm" onClick={() => { setQ(qInput); setPage(1); }}
          style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}>
          <Search size={13} />
        </button>
      </div>

      {/* 목록 */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#475569' }}><Loader2 size={20} className="spin" /></div>
      ) : !data?.rows?.length ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#475569' }}>
          <Archive size={28} style={{ margin: '0 auto 0.5rem', display: 'block', opacity: 0.3 }} />
          {summary?.total ? '검색 결과 없음' : '아직 수집된 보도자료가 없습니다. 위 버튼으로 수집하세요.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {data.rows.map(item => {
            const md = matchData[item.id];
            const isOpen = openId === item.id;
            const isMLd = matchLoading[item.id];
            return (
              <div key={item.id} className="card" style={{ padding: '0.75rem 1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
                  onClick={() => handleMatchItem(item)}>
                  <div style={{ fontSize: '0.72rem', color: '#475569', whiteSpace: 'nowrap' }}>{item.published_at}</div>
                  <div style={{ flex: 1, fontSize: '0.88rem', color: '#e2e8f0', fontWeight: 500 }}>{item.title}</div>
                  {md && (
                    <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap',
                      background: md.count > 0 ? '#14532d' : '#7f1d1d', color: md.count > 0 ? '#4ade80' : '#f87171' }}>
                      {md.count > 0 ? `✅ ${md.count}건 보도` : '❌ 미보도'}
                    </span>
                  )}
                  {isMLd && <Loader2 size={13} className="spin" style={{ color: '#475569' }} />}
                  <ExternalLink size={12} color="#334155" onClick={e => { e.stopPropagation(); window.open(item.url, '_blank'); }} style={{ cursor: 'pointer', flexShrink: 0 }} />
                  {isOpen ? <ChevronUp size={14} color="#475569" /> : <ChevronDown size={14} color="#475569" />}
                </div>

                {isOpen && md && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #1e293b' }}>
                    {md.count === 0 ? (
                      <div style={{ fontSize: '0.82rem', color: '#f87171' }}>아카이브에서 관련 기사를 찾지 못했습니다.</div>
                    ) : (
                      <>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>
                          매칭 키워드: {md.keywords?.map(k => <code key={k} style={{ background: '#1e293b', padding: '1px 5px', borderRadius: '3px', marginRight: '4px', color: '#a78bfa' }}>{k}</code>)}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                          {md.matched?.map(a => (
                            <div key={a.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline', fontSize: '0.82rem' }}>
                              <span style={{ color: '#475569', whiteSpace: 'nowrap', fontSize: '0.72rem' }}>{a.published_at}</span>
                              <span style={{ color: '#38bdf8', whiteSpace: 'nowrap', fontSize: '0.72rem' }}>{a.source}</span>
                              {a.author && <span style={{ color: '#94a3b8', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>{a.author}</span>}
                              <a href={a.link} target="_blank" rel="noreferrer" style={{ color: '#cbd5e1', textDecoration: 'none', flex: 1 }}
                                onMouseOver={e => e.target.style.color='#fff'} onMouseOut={e => e.target.style.color='#cbd5e1'}>
                                {a.title}
                              </a>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 페이지네이션 */}
      {data?.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.35rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          {Array.from({ length: data.pages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #334155', fontSize: '0.8rem',
                background: page === p ? '#7c3aed' : '#0f172a', color: page === p ? '#fff' : '#64748b', cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const SOURCE_TYPE_BADGE = {
  bigkinds: { label: '빅카인즈', bg: '#1e3a5f', color: '#60a5fa' },
  naver:    { label: '네이버',   bg: '#14532d', color: '#4ade80' },
  google:   { label: '구글',     bg: '#3b82f6', color: '#dbeafe' },
  daum:     { label: '다음',     bg: '#ea580c', color: '#ffedd5' },
};
function SourceBadge({ type }) {
  const b = SOURCE_TYPE_BADGE[type] || { label: '기타', bg: '#1e293b', color: '#94a3b8' };
  return (
    <span style={{ fontSize: '0.62rem', padding: '1px 5px', borderRadius: '4px',
      background: b.bg, color: b.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {b.label}
    </span>
  );
}

function parseGoogleTitle(raw) {
  const m = raw.match(/^(.+?)\s+-\s+([^-]+)$/);
  if (m) return { title: m[1].trim(), media: m[2].trim() };
  return { title: raw, media: null };
}

const CONFIDENCE_BADGE = {
  high:   { label: '높음', bg: '#14532d', color: '#4ade80' },
  medium: { label: '검토', bg: '#713f12', color: '#fde047' },
  low:    { label: '낮음', bg: '#7f1d1d', color: '#f87171' },
};

function ConfidenceBadge({ level }) {
  const b = CONFIDENCE_BADGE[level] || CONFIDENCE_BADGE.low;
  return <span style={{ fontSize: '0.6rem', padding: '1px 5px', borderRadius: '4px', background: b.bg, color: b.color, whiteSpace: 'nowrap' }}>{b.label}</span>;
}

function MatchedArticles({ matched, keywords, pressId, onUpdate }) {
  const [deleting, setDeleting] = useState({});
  // 네이버/빅카인즈 제목 집합 (기타 중복 제거용)
  const primaryTitles = new Set(
    matched
      .filter(a => a.source_type === 'bigkinds' || a.source_type === 'naver')
      .map(a => decodeHtml(a.title).replace(/\s+/g, '').slice(0, 25))
  );

  const deduped = matched.filter(a => {
    if (a.source_type === 'bigkinds' || a.source_type === 'naver') return true;
    const { title } = parseGoogleTitle(decodeHtml(a.title));
    return !primaryTitles.has(title.replace(/\s+/g, '').slice(0, 25));
  });

  const countByType = (type) => deduped.filter(a => a.source_type === type).length;
  const bkCount = countByType('bigkinds');
  const naverCount = countByType('naver');
  const googleCount = countByType('google');
  const daumCount = countByType('daum');
  const etcCount = deduped.length - bkCount - naverCount - googleCount - daumCount;

  const countBadge = (label, count, bg, color) => (
    count > 0 ? <span style={{ fontSize: '0.62rem', padding: '1px 6px', borderRadius: '4px', background: bg, color: color }}>{label} {count}</span> : null
  );

  async function handleDelete(article) {
    if (!pressId || !onUpdate) return;
    const reason = window.prompt('삭제 사유를 입력하세요 (예: 무관, 중복, 오매칭):', '무관');
    if (reason === null) return;
    setDeleting(p => ({ ...p, [article.link]: true }));
    try {
      await removeKnfcMatchArticle(pressId, article.link, reason);
      const updated = matched.filter(a => a.link !== article.link);
      onUpdate(updated);
    } catch (e) {
      alert('기사 삭제 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setDeleting(p => ({ ...p, [article.link]: false }));
    }
  }

  return (
    <div style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
      {keywords?.length > 0 && (
        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>매칭 키워드:</span>
          {keywords.map(k => <code key={k} style={{ background: '#1e293b', padding: '1px 5px', borderRadius: '3px', color: '#a78bfa' }}>{k}</code>)}
          <span style={{ marginLeft: '0.5rem', display: 'flex', gap: '0.3rem' }}>
            {countBadge('빅카인즈', bkCount, '#1e3a5f', '#60a5fa')}
            {countBadge('네이버', naverCount, '#14532d', '#4ade80')}
            {countBadge('구글', googleCount, '#3b82f6', '#dbeafe')}
            {countBadge('다음', daumCount, '#ea580c', '#ffedd5')}
            {etcCount > 0 && <span style={{ fontSize: '0.62rem', padding: '1px 6px', borderRadius: '4px', background: '#1e293b', color: '#94a3b8' }}>기타 {etcCount}</span>}
          </span>
        </div>
      )}
      {deduped.map(a => (
        <div key={a.id} style={{ padding: '0.3rem 0', borderTop: '1px solid #1e293b', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '0.7rem', color: '#475569', minWidth: '78px', flexShrink: 0 }}>{a.published_at?.slice(0,10)}</span>
          <SourceBadge type={a.source_type} />
          <span style={{ fontSize: '0.72rem', color: '#38bdf8', minWidth: '70px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.source}</span>
          {a.author && <span style={{ fontSize: '0.7rem', color: '#94a3b8', minWidth: '60px', flexShrink: 0 }}>{a.author}</span>}
          <ConfidenceBadge level={a.confidence_level} />
          <a href={a.link} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '0.8rem', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '0.2rem', flex: 1, minWidth: 0, textDecoration: 'none' }}>
            {decodeHtml(a.title)} <ExternalLink size={10} opacity={0.4} style={{ flexShrink: 0 }} />
          </a>
          {pressId && onUpdate && (
            <button
              onClick={() => handleDelete(a)}
              disabled={deleting[a.link]}
              title="삭제"
              style={{ background: 'none', border: 'none', color: deleting[a.link] ? '#475569' : '#ef4444', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
              {deleting[a.link] ? <Loader2 size={12} className="spin" /> : <Trash2 size={12} />}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function PressMatchPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [openId, setOpenId] = useState(null);
  const [filterMode, setFilterMode] = useState('all');
  const [matchLoading, setMatchLoading] = useState({});
  const [matchResults, setMatchResults] = useState({});
  const [batchMsg, setBatchMsg] = useState('');
  const [matchYears, setMatchYears] = useState(1);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['knfc-press-match', page, q, filterMode],
    queryFn: () => fetchKnfcPress({ page, limit: 30, q, status: filterMode }),
    staleTime: 30000,
  });

  const { data: summary, refetch: refetchSummary } = useQuery({
    queryKey: ['knfc-press-summary'],
    queryFn: fetchKnfcPressSummary,
    refetchInterval: 30000,
  });

  const { data: scraperStatus, refetch: refetchScraper } = useQuery({
    queryKey: ['knfc-scraper-status'],
    queryFn: fetchKnfcScraperStatus,
    refetchInterval: 60000,
  });

  const { data: jobs, refetch: refetchJobs } = useQuery({
    queryKey: ['knfc-match-jobs'],
    queryFn: fetchKnfcMatchJobs,
    refetchInterval: 5000,
  });

  const { data: coverageStats } = useQuery({
    queryKey: ['knfc-coverage-stats'],
    queryFn: () => fetchPressCoverageStats(30),
    refetchInterval: 60000,
  });

  const runningJob = jobs?.jobs?.find(j => j.status === 'running');

  const scrapeMut = useMutation({
    mutationFn: () => scrapeKnfcPress({ max_pages: 20 }),
    onSuccess: (data) => {
      const count = data.count || 0;
      setBatchMsg(count > 0
        ? `✅ 보도자료 수집 완료: ${count}건 신규 추가`
        : `ℹ️ knfc.co.kr 확인 완료: 신규 보도자료 없음`);
      refetch();
      refetchSummary();
      refetchScraper();
    },
    onError: (e) => { setBatchMsg('❌ 보도자료 수집 오류: ' + (e.response?.data?.error || e.message)); },
  });

  async function handleMatchItem(item) {
    // 이미 캐시 있으면 토글만
    if (item.matched !== null || matchResults[item.id]) {
      setOpenId(openId === item.id ? null : item.id);
      return;
    }
    setOpenId(item.id);
    setMatchLoading(p => ({ ...p, [item.id]: true }));
    try {
      const res = await matchKnfcPressItem(item.id);
      setMatchResults(p => ({ ...p, [item.id]: res }));
      qc.invalidateQueries(['knfc-press-match']);
    } catch (e) {
      setMatchResults(p => ({ ...p, [item.id]: { count: 0, matched: [], error: true } }));
    }
    setMatchLoading(p => ({ ...p, [item.id]: false }));
  }

  function StatusBadge({ status }) {
    const config = {
      matched:   { label: '보도', bg: '#14532d', color: '#4ade80' },
      uncovered: { label: '미보도', bg: '#7f1d1d', color: '#f87171' },
      pending:   { label: '미확인', bg: '#713f12', color: '#fde047' },
      failed:    { label: '실패', bg: '#4c1d95', color: '#c4b5fd' },
      stale:     { label: '갱신필요', bg: '#9a3412', color: '#ffedd5' },
    }[status] || { label: status, bg: '#1e293b', color: '#94a3b8' };
    return <span style={{ fontSize: '0.62rem', padding: '1px 6px', borderRadius: '10px', background: config.bg, color: config.color, whiteSpace: 'nowrap' }}>{config.label}</span>;
  }

  async function handleDownloadExcel() {
    try {
      const blob = await downloadPressMatches({ q });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `press_matches_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('엑셀 다운로드 실패: ' + (e.message || '오류'));
    }
  }

  async function handleMatchAll({ rematch = false, since_days = 0 } = {}) {
    setBatchMsg('');
    try {
      const res = await matchAllKnfcPress({ since_years: since_days > 0 ? 0 : matchYears, since_days, rematch });
      const rangeLabel = since_days > 0 ? `최근 ${since_days}일` : matchYears === 0 ? '전체' : `최근 ${matchYears}년`;
      const modeLabel = rematch ? ' (재매칭)' : '';
      setBatchMsg(`✅ [${rangeLabel}${modeLabel}] ${res.queued}건 백그라운드 매칭 시작 (작업 #${res.job_id})`);
      refetchJobs();
    } catch {
      setBatchMsg('❌ 일괄 매칭 오류');
    }
  }

  // 각 아이템의 매칭 결과 (캐시 or 로컬 state 우선)
  function getMatchResult(item) {
    if (matchResults[item.id]) return matchResults[item.id];
    if (item.matched !== null) return { count: item.matched_count, matched: item.matched, keywords: item.keywords };
    return null;
  }

  function updateMatchResult(itemId, newMatched) {
    setMatchResults(prev => {
      const prevResult = prev[itemId];
      if (!prevResult) return prev;
      return {
        ...prev,
        [itemId]: { ...prevResult, count: newMatched.length, matched: newMatched },
      };
    });
    // 서버 캐시와 목록의 cached count도 갱신
    qc.invalidateQueries(['knfc-press-match']);
  }

  const rows = data?.rows || [];
  const filtered = rows; // 서버사이드 필터링 적용됨

  const cachedCount = summary?.cached ?? 0;
  const total = summary?.total ?? 0;

  return (
    <div>
      {/* 상단 통계 + 수집/매칭 버튼 */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '1.5rem', flex: 1, flexWrap: 'wrap' }}>
            {[
              { label: '수집된 보도자료', value: total.toLocaleString(), color: '#e9d5ff', icon: Archive },
              { label: '매칭 완료', value: cachedCount.toLocaleString(), color: '#4ade80', icon: CheckCircle },
              { label: '본문 보강', value: (summary?.with_content || 0).toLocaleString(), color: '#38bdf8', icon: Activity },
              { label: '갱신 필요', value: (summary?.stale || 0).toLocaleString(), color: '#f97316', icon: Clock },
              { label: '실패', value: (summary?.failed || 0).toLocaleString(), color: '#ef4444', icon: ShieldAlert },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                  <s.icon size={14} /> {s.value}
                </div>
                <div style={{ fontSize: '0.68rem', color: '#475569' }}>{s.label}</div>
              </div>
            ))}
            {coverageStats && (
              <div style={{ textAlign: 'left', paddingLeft: '1rem', borderLeft: '1px solid #334155' }}>
                <div style={{ fontSize: '0.72rem', color: '#38bdf8', fontWeight: 600 }}>
                  최근 30일 기사화율: {coverageStats.coverage_rate}%
                </div>
                <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                  전체 {coverageStats.total_press}건 / 보도 {coverageStats.covered} / 미보도 {coverageStats.uncovered} / 실패 {coverageStats.failed}
                </div>
                {coverageStats.response_buckets?.length > 0 && (
                  <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.2rem' }}>
                    소요시간: {coverageStats.response_buckets.map(b => `${b.bucket} ${b.cnt}건`).join(' · ')}
                  </div>
                )}
              </div>
            )}
            {scraperStatus?.status && (
              <div style={{ textAlign: 'left', paddingLeft: '1rem', borderLeft: '1px solid #334155' }}>
                <div style={{ fontSize: '0.72rem', color: scraperStatus.healthy ? '#4ade80' : '#f87171', fontWeight: 600 }}>
                  {scraperStatus.healthy ? '✅ 스크래퍼 정상' : '⚠️ 스크래퍼 이상'}
                </div>
                <div style={{ fontSize: '0.65rem', color: '#64748b' }}>
                  마지막 확인: {scraperStatus.status.last_run_at?.slice(0, 16).replace('T',' ')}<br/>
                  공식 최신: {scraperStatus.status.official_latest_date} / 내부 최신: {scraperStatus.local_latest_date}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => scrapeMut.mutate()} disabled={scrapeMut.isPending}
              style={{ background: '#7c3aed', border: 'none', color: '#fff' }}>
              {scrapeMut.isPending ? <><Loader2 size={12} className="spin" /> 수집 중...</> : <><RefreshCw size={12} /> 보도자료 수집</>}
            </button>
            <select value={matchYears} onChange={e => setMatchYears(Number(e.target.value))}
              style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#94a3b8', padding: '4px 8px', fontSize: '0.78rem', cursor: 'pointer' }}>
              <option value={1}>최근 1년</option>
              <option value={2}>최근 2년</option>
              <option value={3}>최근 3년</option>
              <option value={0}>전체</option>
            </select>
            <button className="btn btn-sm" onClick={() => handleMatchAll()}
              style={{ background: '#065f46', border: 'none', color: '#4ade80' }}>
              <TrendingUp size={12} /> 전체 일괄 매칭
            </button>
            <button className="btn btn-sm" onClick={() => handleMatchAll({ rematch: true })}
              style={{ background: '#9a3412', border: 'none', color: '#ffedd5' }}
              title="선택한 기간의 보도자료를 캐시 무시하고 재검색">
              <RefreshCw size={12} /> 선택 기간 재매칭
            </button>
            <button className="btn btn-sm" onClick={() => handleMatchAll({ rematch: true, since_days: 30 })}
              style={{ background: '#1e3a5f', border: '1px solid #0369a1', color: '#38bdf8' }}
              title="최근 1달 이내 보도자료를 캐시 무시하고 재검색">
              <RefreshCw size={12} /> 최근 1달 재매칭
            </button>
          </div>
        </div>
        {batchMsg && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: batchMsg.startsWith('✅') ? '#4ade80' : batchMsg.startsWith('ℹ️') ? '#38bdf8' : '#f87171' }}>{batchMsg}</div>}
      </div>

      {/* 검색 + 필터 */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" placeholder="제목 검색..." value={qInput} onChange={e => setQInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setQ(qInput); setPage(1); } }}
          style={{ flex: 1, minWidth: '180px' }} />
        <button className="btn btn-sm" onClick={() => { setQ(qInput); setPage(1); }}
          style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}>
          <Search size={13} />
        </button>
        <button className="btn btn-sm" onClick={handleDownloadExcel}
          title="현재 검색 조건의 보도자료 매칭 기사를 엑셀로 다운로드"
          style={{ background: '#14532d', border: '1px solid #16a34a', color: '#4ade80' }}>
          <Download size={13} /> 엑셀
        </button>
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {[['all','전체'], ['reported','보도됨'], ['unreported','미보도'], ['unchecked','미확인'], ['failed','실패'], ['stale','갱신필요']].map(([m, l]) => (
            <button key={m} onClick={() => { setFilterMode(m); setPage(1); }}
              style={{ padding: '3px 10px', borderRadius: '12px', border: '1px solid #334155', fontSize: '0.73rem', background: filterMode === m ? '#1d4ed8' : '#0f172a', color: filterMode === m ? '#fff' : '#64748b', cursor: 'pointer' }}>
              {l} {data?.counts?.[m] !== undefined ? `(${data.counts[m]})` : ''}
            </button>
          ))}
          {runningJob && (
            <span style={{ fontSize: '0.72rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <Loader2 size={12} className="spin" /> 일괄 {runningJob.job_type}: {runningJob.processed}/{runningJob.total} 처리
            </span>
          )}
        </div>
      </div>

      {/* 목록 */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#475569' }}><Loader2 size={20} className="spin" /></div>
      ) : !rows.length ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#475569' }}>
          아직 수집된 보도자료가 없습니다. 위 <strong>보도자료 수집</strong> 버튼을 눌러주세요.
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {filtered.map(item => {
            const mr = getMatchResult(item);
            const isOpen = openId === item.id;
            const isLoading2 = matchLoading[item.id];
            return (
              <div key={item.id} style={{ borderBottom: '1px solid #1e293b', padding: '0.7rem 1rem' }}>
                <div onClick={() => handleMatchItem(item)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  {isLoading2 ? <Loader2 size={15} className="spin" style={{ color: '#64748b', flexShrink: 0 }} />
                    : mr ? (mr.count > 0 ? <CheckCircle size={15} color="#4ade80" style={{ flexShrink: 0 }} /> : <XCircle size={15} color="#f87171" style={{ flexShrink: 0 }} />)
                    : <AlertCircle size={15} color="#475569" style={{ flexShrink: 0 }} />}
                  <span style={{ fontSize: '0.7rem', color: '#475569', whiteSpace: 'nowrap', minWidth: '80px' }}>{item.published_at}</span>
                  <span style={{ flex: 1, fontSize: '0.88rem', color: '#e2e8f0', fontWeight: 500 }}>{item.title}</span>
                  {mr && (
                    <span style={{ fontSize: '0.7rem', padding: '1px 8px', borderRadius: '10px', whiteSpace: 'nowrap', flexShrink: 0,
                      background: mr.count > 0 ? '#14532d' : '#7f1d1d', color: mr.count > 0 ? '#4ade80' : '#f87171', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {mr.count > 0 ? `${mr.count}건` : '미보도'}
                      {item.cache_status && <StatusBadge status={item.cache_status} />}
                    </span>
                  )}
                  <a href={item.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                    <ExternalLink size={12} color="#334155" style={{ flexShrink: 0 }} />
                  </a>
                  {mr?.count > 0 && (isOpen ? <ChevronUp size={13} color="#475569" /> : <ChevronDown size={13} color="#475569" />)}
                </div>
                {isOpen && mr?.matched?.length > 0 && <MatchedArticles matched={mr.matched} keywords={mr.keywords} pressId={item.id} onUpdate={(newMatched) => updateMatchResult(item.id, newMatched)} />}
              </div>
            );
          })}
        </div>
      )}

      {/* 페이지네이션 */}
      {data?.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          {Array.from({ length: data.pages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #334155', fontSize: '0.8rem',
                background: page === p ? '#065f46' : '#0f172a', color: page === p ? '#4ade80' : '#64748b', cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PressPage() {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  const [keyword, setKeyword] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [note, setNote] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [result, setResult] = useState(null);

  const { data: collections = [] } = useQuery({
    queryKey: ['full-collections'],
    queryFn: fetchFullCollections,
    staleTime: 10000,
  });

  const collectMut = useMutation({
    mutationFn: triggerFullCollect,
    onSuccess: (data) => {
      setResult({ count: data.count, id: data.collection_id });
      setSelectedId(data.collection_id);
      qc.invalidateQueries(['full-collections']);
    },
    onError: (e) => setResult({ error: e.response?.data?.error || '오류 발생' }),
  });

  function handleCollect(e) {
    e.preventDefault();
    setResult(null);
    collectMut.mutate({ keyword, date_from: dateFrom, date_to: dateTo, note });
  }

  const [tab, setTab] = useState('match'); // match | archive | monitor

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <Newspaper size={20} color="#a78bfa" />
          보도자료 모니터
        </h1>
      </div>

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem' }}>
        <button onClick={() => setTab('match')}
          style={{ padding: '5px 16px', borderRadius: '6px', border: '1px solid #334155', fontSize: '0.85rem', background: tab === 'match' ? '#065f46' : '#0f172a', color: tab === 'match' ? '#4ade80' : '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <TrendingUp size={13} /> 보도 실적 대조
        </button>
        <button onClick={() => setTab('archive')}
          style={{ padding: '5px 16px', borderRadius: '6px', border: '1px solid #334155', fontSize: '0.85rem', background: tab === 'archive' ? '#7c3aed' : '#0f172a', color: tab === 'archive' ? '#e9d5ff' : '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <Archive size={13} /> 보도자료 아카이브
        </button>
        <button onClick={() => setTab('monitor')}
          style={{ padding: '5px 16px', borderRadius: '6px', border: '1px solid #334155', fontSize: '0.85rem', background: tab === 'monitor' ? '#1d4ed8' : '#0f172a', color: tab === 'monitor' ? '#fff' : '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <Search size={13} /> 수집 모니터
        </button>
      </div>

      {tab === 'match' && <PressMatchPage />}
      {tab === 'archive' && <KnfcArchivePage />}

      {tab === 'monitor' && <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.25rem', alignItems: 'start' }}>
        {/* 왼쪽: 수집 폼 + 이력 */}
        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h2 className="section-title"><Search size={15} /> 전수 수집</h2>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '1rem', lineHeight: 1.6 }}>
              키워드가 포함된 모든 기사를 <strong>중복 필터 없이</strong> 수집합니다.<br />
              매체별 보도 현황을 한눈에 확인하세요.
            </p>
            <form onSubmit={handleCollect} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.25rem' }}>키워드 *</div>
                <input className="input" placeholder="예: 한수원 보도자료" value={keyword}
                  onChange={(e) => setKeyword(e.target.value)} required style={{ width: '100%' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.25rem' }}>시작일</div>
                  <input className="input" type="date" value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.25rem' }}>종료일</div>
                  <input className="input" type="date" value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)} style={{ width: '100%' }} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.25rem' }}>메모 (선택)</div>
                <input className="input" placeholder="예: 6월 SMR 보도자료 배포 건" value={note}
                  onChange={(e) => setNote(e.target.value)} style={{ width: '100%' }} />
              </div>
              <button className="btn btn-primary btn-sm" type="submit" disabled={collectMut.isPending || !keyword}>
                {collectMut.isPending ? <><Loader2 size={14} className="spin" /> 수집 중...</> : <><Search size={14} /> 전수 수집 시작</>}
              </button>
            </form>
            {result && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: result.error ? '#f87171' : '#4ade80' }}>
                {result.error ? `❌ ${result.error}` : `✅ ${result.count}건 수집 완료 (#${result.id})`}
              </div>
            )}
          </div>

          {/* 수집 이력 */}
          <div className="card">
            <h2 className="section-title" style={{ marginBottom: '0.75rem' }}>수집 이력</h2>
            {collections.length === 0 ? (
              <div style={{ color: '#475569', fontSize: '0.85rem' }}>아직 수집 이력이 없습니다.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '400px', overflowY: 'auto' }}>
                {collections.map((c) => (
                  <div key={c.id}
                    onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
                    style={{
                      padding: '0.5rem 0.65rem', borderRadius: '6px', cursor: 'pointer',
                      border: `1px solid ${selectedId === c.id ? '#6366f1' : '#1e293b'}`,
                      background: selectedId === c.id ? '#1e1b4b' : '#0f172a',
                    }}
                  >
                    <div style={{ fontSize: '0.82rem', color: '#e2e8f0', fontWeight: 600 }}>"{c.keyword}"</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.15rem' }}>
                      {c.date_from} ~ {c.date_to} · {c.article_count}건
                    </div>
                    {c.note && <div style={{ fontSize: '0.7rem', color: '#475569', marginTop: '0.1rem' }}>{c.note}</div>}
                    <div style={{ fontSize: '0.67rem', color: '#334155', marginTop: '0.1rem' }}>{c.created_at?.slice(0, 16)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 오른쪽: 결과 */}
        <div className="card">
          {!selectedId ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#475569' }}>
              <Newspaper size={32} style={{ margin: '0 auto 0.75rem', display: 'block', opacity: 0.3 }} />
              왼쪽에서 수집을 실행하거나 이력을 선택하세요.
            </div>
          ) : (
            <CollectionDetail id={selectedId} />
          )}
        </div>
      </div>

      }

      <style>{`
        .spinner {
          width: 16px; height: 16px;
          border: 2px solid #334155; border-top-color: #38bdf8;
          border-radius: 50%; animation: spin 0.7s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
